import MDEditor from "@uiw/react-md-editor"
import "@uiw/react-md-editor/markdown-editor.css"
import type { PropertyDTO } from "@zplanem/responses"
import { useMemo, useState } from "react"
import { MessageMarkdown } from "../../../components/chat/message-markdown"
import { getOwnedOrigins } from "../../../lib/owned-origins.runtime"
import { cn } from "../../../lib/utils"
import { useUpdateProperty } from "../../hooks/use-update-property"
import { getTranslations } from "../../i18n"
import { useLocale } from "../../locale-context"
import { useTheme } from "../../theme-context"

function useColorMode(): "dark" | "light" {
	const { mode } = useTheme()
	const prefersDark =
		typeof window !== "undefined" &&
		window.matchMedia("(prefers-color-scheme: dark)").matches
	return mode === "dark" || (mode === "auto" && prefersDark) ? "dark" : "light"
}

/**
 * The full Markdown description, rendered read-only and — for the owner —
 * editable in place (ZPL-186): hover shows a text cursor, click opens a
 * Markdown editor with an "Improve with AI" toggle and a Save bar that sticks
 * to the bottom of the screen (above the mobile bottom nav) when the content
 * is taller than the viewport.
 */
export function EditablePropertyDescription({
	property,
	canEdit,
}: {
	property: PropertyDTO
	canEdit: boolean
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale).properties.edit
	const colorMode = useColorMode()
	const ownedOrigins = useMemo(getOwnedOrigins, [])
	const [editing, setEditing] = useState(false)
	const [draft, setDraft] = useState(property.description ?? "")
	const [improve, setImprove] = useState(false)
	const mutation = useUpdateProperty(property.id)

	function start() {
		setDraft(property.description ?? "")
		setImprove(false)
		setEditing(true)
	}

	async function save() {
		try {
			await mutation.mutateAsync({ description: draft, improve })
		} finally {
			setEditing(false)
		}
	}

	if (canEdit && editing) {
		return (
			<div className="mt-6">
				<div data-color-mode={colorMode}>
					<MDEditor
						value={draft}
						onChange={(v) => setDraft(v ?? "")}
						height={400}
						textareaProps={{ placeholder: t.descriptionPlaceholder }}
					/>
				</div>
				{/* Sticks above the mobile bottom nav (h-16) / the screen bottom on
				    desktop when the page is taller than the viewport. */}
				<div className="sticky bottom-16 md:bottom-0 z-10 mt-3 flex items-center gap-3 border-t border-border bg-background/95 px-1 py-3 backdrop-blur">
					<label className="mr-auto flex items-center gap-2 text-sm text-foreground/80">
						<input
							type="checkbox"
							checked={improve}
							onChange={(e) => setImprove(e.target.checked)}
							className="h-4 w-4 accent-primary"
						/>
						{t.improveWithAi}
					</label>
					<button
						type="button"
						onClick={() => setEditing(false)}
						disabled={mutation.isPending}
						className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent disabled:opacity-50"
					>
						{t.cancel}
					</button>
					<button
						type="button"
						onClick={save}
						disabled={mutation.isPending}
						className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
					>
						{mutation.isPending ? t.saving : t.save}
					</button>
				</div>
			</div>
		)
	}

	if (!property.description) {
		if (!canEdit) return null
		return (
			<button
				type="button"
				onClick={start}
				className="mt-6 cursor-text text-base text-muted-foreground hover:text-foreground"
			>
				{t.addDescription}
			</button>
		)
	}

	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: Notion-style click-to-edit region; inner links still navigate via the closest("a") guard.
		<div
			className={cn(
				"mt-6 text-base text-foreground/80 leading-relaxed",
				canEdit && "cursor-text",
			)}
			title={canEdit ? t.editDescription : undefined}
			onClick={
				canEdit
					? (e) => {
							// Let links inside the description navigate normally.
							if ((e.target as HTMLElement).closest("a")) return
							start()
						}
					: undefined
			}
			onKeyDown={
				canEdit
					? (e) => {
							if (e.key === "Enter") start()
						}
					: undefined
			}
		>
			<MessageMarkdown ownedOrigins={ownedOrigins}>
				{property.description}
			</MessageMarkdown>
		</div>
	)
}
